import React from 'react';

import { createUploadWorker } from './createUploadWorker';

export type UploadWorkerHandlers = {
  onMessage: (event: MessageEvent) => void;
  // `Event`, not `ErrorEvent`: lib.dom types `Worker.onerror` as an `ErrorEvent`,
  // but a worker whose own script fails to fetch fires a plain `Event` with no
  // `message` (sen-8 U5). The handler must not assume the fields exist.
  onError: (event: Event) => void;
  /**
   * Fired synchronously right after a worker is constructed, before its
   * listeners are attached — the one moment nothing from that worker can have
   * arrived yet (sen-8 U5: the stale-chunk recovery keys readiness per worker).
   */
  onStart?: () => void;
  /**
   * WHOSE worker this is. Changing it terminates the worker and builds a new one, because
   * the running one is uploading the previous account's files into the previous account's
   * organization (PR-2.1: the provider is no longer remounted every time the identity
   * moves). Left out, the worker simply lives as long as the mount, which is what every
   * caller outside the session providers wants.
   *
   * A counter is the honest form for it: the account KEY moves once on every cold boot,
   * as the tenant and then the account settle, and that is not a new account.
   */
  ownerKey?: string | number;
};

/**
 * Owns the upload worker's lifetime: one worker per mount and per `ownerKey`, terminated
 * on unmount and whenever that key moves.
 *
 * Both listeners go on the worker in the SAME effect that creates it, before the
 * state update that publishes it. A dedicated worker's outside port is enabled
 * the moment its script finishes, and a `message` task is then dispatched whether
 * or not anything listens — the worker posts `OnReady` from its module top, and
 * installing `onmessage` in a later effect (after `setWorker` had committed a
 * re-render) let that proof-of-recovery be delivered to nothing, stranding the
 * stale-chunk worker guard for the rest of the tab session (sen-7 T3). The
 * handlers are read through a ref so the latest ones apply without re-binding.
 */
export const useUploadWorker = ({
  onMessage,
  onError,
  onStart,
  ownerKey,
}: UploadWorkerHandlers) => {
  const [worker, setWorker] = React.useState<Worker | null>(null);
  const handlersRef = React.useRef<UploadWorkerHandlers>({
    onError,
    onMessage,
    onStart,
  });

  React.useEffect(() => {
    handlersRef.current = { onError, onMessage, onStart };
  }, [onError, onMessage, onStart]);

  React.useEffect(() => {
    const created = createUploadWorker();
    // Disposal is not the same event as termination. `terminate()` stops the worker, but
    // a message it dispatched BEFORE the account changed is already on its way to a
    // handler that now belongs to somebody else — so the port is closed here, in the
    // listeners, the moment this worker stops being the current one.
    let isDisposed = false;
    handlersRef.current.onStart?.();
    created.onerror = (event) => {
      if (isDisposed) {
        // Chromium still reports the `importScripts` a terminated worker was in the middle
        // of, and an uncancelled ErrorEvent is re-raised on the page's `window.onerror` —
        // Sentry V2-30, right after every login that swapped the worker. We ended it.
        event.preventDefault();
        return;
      }
      handlersRef.current.onError(event);
    };
    created.onmessage = (event) => {
      if (isDisposed) return;
      handlersRef.current.onMessage(event);
    };
    setWorker(created);

    return () => {
      isDisposed = true;
      created.terminate();
    };
  }, [ownerKey]);

  return worker;
};
